/* eslint-disable react-hooks/exhaustive-deps */
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Collapse } from '@material-ui/core';
import { useState, useMemo } from 'react';
import H3 from '@base/H3/H3';
import H4 from '@base/H4/H4';
import Button from '@base/Button/Button';
import QuestionPreview from '@educator/Composite/QuestionPreview/QuestionPreview';
import RemoveCircleOutline from '@icon/RemoveCircleOutline';
import AddCircleOutline from '@icon/AddCircleOutline';
import { useSelectorToStore, useDispatchToStore } from '@hooks/reduxHooks';
import useTimeout from '@hooks/useTimeout';
import pushRoute from '@utils/pushRoute';
import {
  saveQuestion,
  editContent,
  markCorrect,
  markIsCommon
} from '@actions/question';
import Options from '@educator/Container/PracticeQuestionEditor/Options';
import ContentEditor from '@educator/Container/PracticeQuestionEditor/ContentEditor';
import { BgColors, Spacings } from '@styles/index';
import { QUESTION_TYPE_MAP } from '@constants/quiz';

const isEmpty = (value) =>
  value === undefined || value === '' || value === null;

const Wrapper = styled.div`
  display: flex;
  grid-column: span 24;
`;

const Unauthorised = styled(H4)`
  grid-column: span 24;
`;

const LeftWrapper = styled.div`
  width: 360px;
  position: fixed;
`;

const RightWrapper = styled.div`
  width: 720px;
  margin-left: auto;
`;

const Flex = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${Spacings.SPACING_5B};
  background-color: ${BgColors.BASE};
  margin-top: ${({ showMargin }) => showMargin && Spacings.SPACING_8B};
  & > :not(:last-child) {
    margin-right: ${Spacings.SPACING_8B};
  }
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const QuestionType = styled.div`
  padding: ${Spacings.SPACING_4B};
  background-color: ${BgColors.OFF_WHITE};
`;

const Remove = styled(RemoveCircleOutline)`
  cursor: pointer;
`;

const Add = styled(AddCircleOutline)`
  cursor: pointer;
`;

const addNew = () => window.open('/educator/question/add', '_blank');

const getDataToDuplicate = (data) => {
  const { selectedLanguages } = data;
  const hint = {};
  const q_content = {};
  const solution_explanation = {};
  const comprehension = {};
  const single_solution = {};
  const upper_bound = {};
  const lower_bound = {};
  selectedLanguages.forEach(({ id }) => {
    hint[id] = data.hint[id];
    q_content[id] = data.question[id];
    solution_explanation[id] = data.solutionExplanation[id];
    comprehension[id] = data.passage[id];
    single_solution[id] = data.singleSolution?.[id];
    upper_bound[id] = data.upperLimit?.[id];
    lower_bound[id] = data.lowerLimit?.[id];
  });
  const newData = {
    descriptive_question_type: data.descriptiveQuestionType,
    difficulty_level: data.difficultyLevel,
    expected_time: data.expectedTime,
    hint,
    video_solution: data.videoSolution,
    has_diagram: data.hasDiagram,
    has_equation: data.hasEquation,
    q_content,
    q_uid: null,
    q_type: data.questionType,
    single_solution:
      data.questionType === QUESTION_TYPE_MAP.INTEGER ? single_solution : {},
    comprehension: data.hasPassage ? comprehension : null,
    upper_bound:
      data.questionType === QUESTION_TYPE_MAP.INTEGER && data.isIntegerRange
        ? upper_bound
        : {},
    lower_bound:
      data.questionType === QUESTION_TYPE_MAP.INTEGER && data.isIntegerRange
        ? lower_bound
        : {},
    duplicate_from_question: data.questionUID,
    answers:
      data.questionType === QUESTION_TYPE_MAP.INTEGER
        ? []
        : data.options.map((option, index) => {
            const ans_c = {};
            selectedLanguages.forEach(({ id }) => {
              ans_c[id] = option[id];
            });
            return {
              ans_c,
              rank: index + 1,
              is_correct: data?.correctAnswer.includes(index + 1),
              is_common_mistake: data.isCommonMistakes[index],
              mistake_hint: data.mistakeHints[index]
            };
          }),
    solution_explanation
  };
  if (newData.hint === '<p><br></p>') newData.hint = '';
  return newData;
};

const getDataToSave = (data) => ({
  ...getDataToDuplicate(data),
  exam_year: data.isPreviousYearQuestion ? data.examYear : '',
  exam_uid: data.isPreviousYearQuestion ? data?.exam?.uid : null,
  topology_uid: data.primarySubConcept.uid,
  secondary_concept_uid: data?.secondarySubConcept?.uid,
  goal_uid: data.goal.uid,
  duplicate_from_question: null,
  q_uid: data.questionUID
});

const validate = (data) => {
  let res = {};
  const {
    noPreference,
    question,
    selectedLanguages,
    questionType,
    isPreviousYearQuestion,
    exam,
    examYear,
    correctAnswer,
    solutionExplanation,
    videoSolution,
    options,
    primarySubConcept,
    hasPassage,
    isIntegerRange,
    integerDataType,
    lowerLimit,
    upperLimit,
    passage,
    singleSolution
  } = data;
  if (noPreference.length)
    res = {
      status: false,
      error: 'You need to select a language for "No preference" tab(s)'
    };
  else if (questionType !== 3 && !correctAnswer.length)
    res = {
      status: false,
      error: 'You need to select a correct option choice before proceeding'
    };
  else if (
    questionType !== 3 &&
    isPreviousYearQuestion &&
    (!examYear || !exam)
  ) {
    res = {
      status: false,
      error: 'You need to select a Exam name and Year'
    };
  } else if (!primarySubConcept)
    res = {
      status: false,
      error: 'You need to select a correct concept before proceeding'
    };
  else {
    selectedLanguages.forEach(({ id, name }) => {
      if (!question[id] || question[id] === '<p><br></p>')
        res = { status: false, error: `Question in ${name} language is empty` };
    });
    if (!res.error) {
      let lang = null;
      if (questionType !== 3) {
        options.forEach((option) => {
          selectedLanguages.forEach(({ id, name }) => {
            if (!option[id] || option[id] === '<p><br></p>')
              lang = { id, name };
          });
        });
      }
      if (!lang) res.status = true;
      else
        res = {
          status: false,
          error: `Option in ${lang.name} language is empty`
        };
    }
  }

  if (!videoSolution) {
    selectedLanguages.forEach(({ id, name }) => {
      if (
        !solutionExplanation[id] ||
        solutionExplanation[id] === '<p><br></p>'
      ) {
        res = {
          status: false,
          error: `Solution (or video solution) in ${name} language is empty`
        };
        return res;
      }
    });
  }

  if (hasPassage) {
    selectedLanguages.forEach(({ id, name }) => {
      if (!passage[id] || passage[id] === '<p><br></p>') {
        res = {
          status: false,
          error: `Passage in ${name} language is empty`
        };
      }
    });
  }

  if (questionType === QUESTION_TYPE_MAP.INTEGER) {
    selectedLanguages.forEach(({ id, name }) => {
      if (isEmpty(singleSolution[id])) {
        res = {
          status: false,
          error: `You need to write a correct integer input answer in ${name} language before proceeding`
        };
      }
    });
  }

  if (
    questionType === QUESTION_TYPE_MAP.INTEGER &&
    isIntegerRange &&
    !integerDataType &&
    (Object.keys(lowerLimit).length !== selectedLanguages.length ||
      Object.keys(upperLimit).length !== selectedLanguages.length)
  ) {
    res = {
      status: false,
      error: 'Please specify the range'
    };
  }

  if (
    questionType === QUESTION_TYPE_MAP.INTEGER &&
    !integerDataType &&
    isIntegerRange
  ) {
    selectedLanguages.forEach(({ id, name }) => {
      if (
        isEmpty(lowerLimit[id]) ||
        isEmpty(upperLimit[id]) ||
        parseInt(lowerLimit[id], 10) > parseInt(singleSolution[id], 10) ||
        parseInt(upperLimit[id], 10) < parseInt(singleSolution[id], 10) ||
        parseInt(upperLimit[id], 10) < parseInt(lowerLimit[id], 10)
      ) {
        res = {
          status: false,
          error: `Answer for ${name} language should be inside the defined range. Please verify`
        };
      }
    });
  }

  return res;
};

const duplicate = (data, dispatch) => () => {
  const validated = validate(data);

  if (validated.status) {
    dispatch(saveQuestion(getDataToDuplicate(data))).then((response) => {
      if (response.result)
        window.open(`/educator/question/edit/${response.result.uid}`);
    });
  } else if (validated?.error) {
    dispatch({
      type: 'SET_SNACKBAR_MESSAGE',
      message: validated.error,
      messageType: 'error'
    });
  }
};

const handleSave = (data, dispatch) => () => {
  const validated = validate(data);
  if (validated.status)
    dispatch(saveQuestion(getDataToSave(data))).then((response) => {
      if (response.result) {
        dispatch({
          type: 'SET_SNACKBAR_MESSAGE',
          message: 'Question successfully saved',
          messageType: 'success'
        });
        pushRoute(
          '/educator/question/edit/[questionUID]',
          `/educator/question/edit/${response.result.uid}`
        );
      } else
        dispatch({
          type: 'SET_SNACKBAR_MESSAGE',
          message: 'Something went wrong. Please try again',
          messageType: 'error'
        });
    });
  if (validated?.error)
    dispatch({
      type: 'SET_SNACKBAR_MESSAGE',
      message: validated.error,
      messageType: 'error'
    });
};

const renderButtons = (isEditForm, data, showMargin, dispatch) => (
  <Flex showMargin={showMargin}>
    {isEditForm && (
      <Button
        label="Duplicate"
        theme="blue"
        grid={3}
        onClick={duplicate(data, dispatch)}
      />
    )}
    <Button
      label="Save"
      theme="blue"
      grid={3}
      onClick={handleSave(data, dispatch)}
    />
    {isEditForm && (
      <Button
        label="Add new question"
        theme="black"
        variant="hollow"
        onClick={addNew}
      />
    )}
  </Flex>
);

const onChange = (dispatch) => (id, language) => (editorState) =>
  dispatch(editContent({ value: editorState, language, id }));

const expand = (setIsExpanded) => () => setIsExpanded(true);
const contract = (setIsExpanded) => () => setIsExpanded(false);

const handleMarkCorrect = (rank, isMultipleCorrect, dispatch) => () =>
  dispatch(markCorrect(rank, isMultipleCorrect));

const handleIsCommon = (index, dispatch) => () => dispatch(markIsCommon(index));

const QuestionEditor = ({ isEditForm, isServer }) => {
  const dispatch = useDispatchToStore();
  const mapStateToProps = ({ createQuestion, auth }) => ({
    data: createQuestion,
    isModerator: auth.me?.isModerator,
    isExternalContentCreator: auth.me?.isExternalContentCreator
  });
  const { data, isModerator, isExternalContentCreator } = useSelectorToStore(
    mapStateToProps
  );
  const [isExpanded, setIsExpanded] = useState(true);
  const [mounted, setMounted] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState(
    data.selectedLanguages[0]?.id
  );

  const selectedLanguageName = useMemo(() => {
    if (!data.selectedLanguages?.length) {
      return '';
    }

    return (data?.allLanguages || []).find(
      (language) => language.id === selectedLanguage
    )?.name;
  }, [data?.allLanguages, data.selectedLanguages, selectedLanguage]);

  useTimeout(() => {
    if (mounted) setMounted(false);
  }, 0);
  if (isServer && mounted) return null;
  if (!isModerator && !isExternalContentCreator)
    return (
      <Unauthorised>
        You do not have permission to perform this action
      </Unauthorised>
    );
  return (
    <Wrapper>
      <LeftWrapper>
        <H3>
          {selectedLanguageName ? `In ${selectedLanguageName}` : 'Add question'}
        </H3>
        <QuestionPreview data={data} selectedLanguage={selectedLanguage} />
      </LeftWrapper>
      <RightWrapper>
        {renderButtons(isEditForm, data, false, dispatch)}
        <QuestionType>
          <Option>
            <H4>Options</H4>
            {isExpanded ? (
              <Remove size="24px" onClick={contract(setIsExpanded)} />
            ) : (
              <Add size="24px" onClick={expand(setIsExpanded)} />
            )}
          </Option>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Options data={data} isEditForm={isEditForm} />
          </Collapse>
        </QuestionType>
        {data.noPreference.length || data.selectedLanguages.length ? (
          <ContentEditor
            data={data}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            onChange={onChange(dispatch)}
            handleMarkCorrect={handleMarkCorrect}
            handleIsMistake={handleIsCommon}
          />
        ) : null}
        {renderButtons(
          isEditForm,
          data,
          data.noPreference.length === 0 && data.selectedLanguages.length === 0,
          dispatch
        )}
      </RightWrapper>
    </Wrapper>
  );
};

export default QuestionEditor;

QuestionEditor.propTypes = {
  isEditForm: PropTypes.bool,
  isServer: PropTypes.bool.isRequired
};

QuestionEditor.defaultProps = {
  isEditForm: false
};
